import { supabaseClient } from './supabase';

export type SavedScenario = {
  id: string;
  name?: string | null;
  description?: string | null;
  created_by?: string | null;
  is_public?: boolean;
  location: string;
  transport_scenario?: string | null;
  buildings_scenario?: string | null;
  local_preset?: string | null;
  scenario_json: Record<string, unknown>;
  cost_assumptions?: Record<string, unknown> | null;
  results_json?: Record<string, unknown> | null;
  model_version?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type SaveScenarioInput = Omit<SavedScenario, 'id' | 'created_at' | 'updated_at'>;
export type UpdateScenarioInput = Partial<Omit<SavedScenario, 'id' | 'created_at' | 'updated_at'>> & { id: string };

const TABLE = 'scenarios';

function isDev(): boolean {
  return typeof process !== 'undefined' && process.env.NODE_ENV === 'development';
}

function devLog(message: string, payload?: unknown): void {
  if (!isDev()) return;
  if (payload !== undefined) console.log(`[scenarioService] ${message}`, payload);
  else console.log(`[scenarioService] ${message}`);
}

export async function saveScenario(input: SaveScenarioInput): Promise<SavedScenario> {
  if (!input.created_by) {
    throw new Error('Must be logged in to save a scenario');
  }
  const { data, error } = await supabaseClient
    .from(TABLE)
    .insert({
      name: input.name ?? null,
      description: input.description ?? null,
      created_by: input.created_by,
      is_public: input.is_public ?? false,
      location: input.location,
      transport_scenario: input.transport_scenario ?? null,
      buildings_scenario: input.buildings_scenario ?? null,
      local_preset: input.local_preset ?? null,
      scenario_json: input.scenario_json,
      cost_assumptions: input.cost_assumptions ?? null,
      results_json: input.results_json ?? null,
      model_version: input.model_version ?? null,
    })
    .select('*')
    .single();

  if (error || !data) {
    throw new Error(error?.message || 'Failed to save scenario');
  }
  devLog('saved scenario', { id: data.id });
  return data as SavedScenario;
}

export async function loadScenarioById(id: string): Promise<SavedScenario | null> {
  const { data, error } = await supabaseClient
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    throw new Error(error.message || 'Failed to load scenario');
  }
  if (!data) return null;
  devLog('loaded scenario by id', { id });
  return data as SavedScenario;
}

export async function updateScenario(input: UpdateScenarioInput): Promise<SavedScenario> {
  const updatePayload: Record<string, unknown> = {};
  if (input.name !== undefined) updatePayload.name = input.name;
  if (input.description !== undefined) updatePayload.description = input.description;
  if (input.created_by !== undefined) updatePayload.created_by = input.created_by;
  if (input.is_public !== undefined) updatePayload.is_public = input.is_public;
  if (input.location !== undefined) updatePayload.location = input.location;
  if (input.transport_scenario !== undefined) updatePayload.transport_scenario = input.transport_scenario;
  if (input.buildings_scenario !== undefined) updatePayload.buildings_scenario = input.buildings_scenario;
  if (input.local_preset !== undefined) updatePayload.local_preset = input.local_preset;
  if (input.scenario_json !== undefined) updatePayload.scenario_json = input.scenario_json;
  if (input.cost_assumptions !== undefined) updatePayload.cost_assumptions = input.cost_assumptions;
  if (input.results_json !== undefined) updatePayload.results_json = input.results_json;
  if (input.model_version !== undefined) updatePayload.model_version = input.model_version;

  const { data, error } = await supabaseClient
    .from(TABLE)
    .update(updatePayload)
    .eq('id', input.id)
    .select('*')
    .single();

  if (error || !data) {
    throw new Error(error?.message || 'Failed to update scenario');
  }
  devLog('updated scenario', { id: input.id });
  return data as SavedScenario;
}

/** Optional helper for future UI. */
export async function maybeDeleteScenario(id: string): Promise<boolean> {
  const { error } = await supabaseClient
    .from(TABLE)
    .delete()
    .eq('id', id);
  if (error) {
    devLog('delete scenario failed', { id, error: error.message });
    return false;
  }
  devLog('deleted scenario', { id });
  return true;
}
